import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import roboOnImage from '../pages/images/robo_on.jpg';
import roboOffImage from '../pages/images/robo_off.jpg';
import { fetchNewtelemetry, fetchOneHourTotalTelemetry } from '../pages/api';
import ProgressSemiCircle from '../components/ProgressSemiCircle'; // 新しく追加  
import { API_URL } from '../pages/api'; // api.js からインポート  

const CardContainer = styled.div`  
  margin-bottom: 20px;  
  display: grid;  
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));  
  gap: 20px;  
  /* ギャップを広げる */  
`;

const Card = styled(Link)`  
  display: flex;  
  align-items: center;  
  justify-content: space-between;  
  border-radius: 15px;  
  /* 角丸を大きく */  
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);  
  /* 影を調整 */  
  background-color: #ffffff;  
  /* 背景色を白に */  
  padding: 16px;  
  color: #333333;  
  text-decoration: none;  
  transition: transform 0.2s;  
  &:hover {  
    transform: scale(1.05);  
    /* ホバー時に少し拡大 */  
  }  
  @media (max-width: 768px) {  
    flex-direction: row;  
    align-items: center;  
  }  
`;

const ImagesContainer = styled.div`  
  display: flex;  
  justify-content: center;  
  margin-bottom: 10px;  
  @media (max-width: 768px) {  
    margin-bottom: 0;  
    margin-right: 10px;  
  }  
`;

const Img = styled.img`  
  border-radius: 10px;  
  width: 100px;  
  height: 100px;  
  object-fit: cover;  
  margin: 0 8px;  
  @media (max-width: 768px) {  
    margin: 0;  
  }  
`;

const CardDetails = styled.div`  
  display: flex;  
  flex-direction: column;  
  @media (max-width: 768px) {  
    align-items: flex-start;  
  }  
`;

const H3 = styled.h3`  
  font-size: 18px;  
  /* フォントサイズを少し小さめに */  
  font-weight: bold;  
  margin: 0 0 5px 0;  
  word-break: break-word;  
  color: green;  
  /* タイトルの色を緑に */  
  @media (max-width: 768px) {  
    margin: 0 0 4px 0;  
  }  
`;

const P = styled.p`  
  margin: 2px 0;  
  font-size: 12px;  
  /* フォントサイズを調整 */  
  color: #666666;  
`;

const Arrow = styled.div`  
  font-size: 24px;  
  color: green;  
  font-weight: bold;  
  margin-left: 16px;  
  align-self: center;  
`;

const zoomInOut = keyframes`  
  0% {  
    transform: scale(1);  
  }  
  50% {  
    transform: scale(1.2);  
  }  
  100% {  
    transform: scale(1);  
  }  
`;

const AnimatedImage = styled.img`  
  border-radius: 10px;  
  width: 100px;  
  height: 100px;  
  object-fit: cover;  
  margin: 0 8px;  
  animation: ${zoomInOut} 2s infinite;  
  @media (max-width: 480px) {  
    margin: 0;  
  }  
`;

const SelectMachine = ({ robots }) => {
  const [robotStatuses, setRobotStatuses] = useState({});
  const [robotTelemetry, setRobotTelemetry] = useState({});
  const [totalSuccess, setRobotOneHourTelemetry] = useState({});
  const [progress, setProgress] = useState(0.5); // 追加: 進捗率の初期値を50%に設定  

  const fetchStatus = async (id) => {
    try {
      const response = await fetch(`${API_URL}/status?hubName=command_hub_${id}`, { method: 'GET' });
      if (response.status === 404) {
        return 'オフ';
      } else {
        return '稼働中';
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      return 'サーバーエラー';
    }
  };

  const fetchTelemetry = async (id) => {
    try {
      const telemetryData = await fetchNewtelemetry(id, 1); // 適切なレコード数を指定  
      return telemetryData;
    } catch (error) {
      console.error('Error fetching telemetry:', error);
      return [];
    }
  };

  const fetchOneHourTelemetry = async (id) => {
    try {
      const telemeOneHourtryData = await fetchOneHourTotalTelemetry(id, 1); // パラメータ 'days' を追加  
      return telemeOneHourtryData;
    } catch (error) {
      console.error('Error fetching telemetry:', error);
      return [];
    }
  };

  useEffect(() => {
    const updateStatusesAndTelemetry = async () => {
      const statuses = {};
      const telemetry = {};
      const totalSuccess = {};
      let activeRobotsCount = 0;

      for (const robot of robots) {
        if (robot.greenhouse) {
          const status = await fetchStatus(robot.name);
          statuses[robot.name] = status;

          if (status === '稼働中') {
            activeRobotsCount++;
          }

          const telemetryData = await fetchTelemetry(robot.name);
          telemetry[robot.name] = {
            data: telemetryData,
            battery: telemetryData.length > 0 ? telemetryData[0].battery : 'N/A'
          };

          const telemeOneHourtryData = await fetchOneHourTelemetry(robot.name);
          totalSuccess[robot.name] = telemeOneHourtryData ? telemeOneHourtryData.reduce((sum, item) => sum + (item.eventCounts.ok || 0), 0) : 'N/A';
        }
      }

      const progressValue = robots.length > 0 ? activeRobotsCount / robots.length : 0;
      setProgress(progressValue);

      setRobotStatuses(statuses);
      setRobotTelemetry(telemetry);
      setRobotOneHourTelemetry(totalSuccess);
    };

    updateStatusesAndTelemetry();
    const interval = setInterval(updateStatusesAndTelemetry, 60000); // 1分ごとにステータスとテレメトリを更新  

    return () => clearInterval(interval);
  }, [robots]);

  return (
    <>
      <CardContainer>
        {robots.map((robot, index) => (
          robot.greenhouse && (
            <Card to={`/${robot.name}`} key={index}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ImagesContainer>
                  {robotStatuses[robot.name] === '稼働中' ? (
                    <AnimatedImage src={roboOnImage} alt={robot.name} />
                  ) : (
                    <Img src={roboOffImage} alt={robot.name} />
                  )}
                </ImagesContainer>
                <CardDetails>
                  <H3>{robot.name}</H3>
                  <P>{robotStatuses[robot.name] || '読み込み中...'}</P>
                  <P>1日の収穫量: {totalSuccess[robot.name] || 0} 個</P>
                  <P>バッテリー: {robotTelemetry[robot.name]?.battery || 'N/A'}%</P>
                </CardDetails>
              </div>
              <Arrow>＞</Arrow>
            </Card>
          )
        ))}
      </CardContainer>
      {robots.length > 0 && (  
        <CardContainer>  
          <ProgressSemiCircle progress={progress} />  
        </CardContainer>  
      )}  
    </>
  );
};

export default SelectMachine;  
